import React, { FC, useState } from "react";
import styled from "styled-components";

import { useConfirm } from "../../../../ui/components/ConfirmDialog";

import { AdminTools } from "./AdminTools";
import { EditFields } from "./EditFields";
import { SummaryStrip } from "./SummaryStrip";
import { Timeline } from "./Timeline";
import {
  Body,
  Btn,
  Disclosure,
  ErrorText,
  Hint,
  Input,
  Pill,
  SectionTitle,
  Shell,
  ShellHeader,
  colors,
} from "./styles";
import { todayDayKey, useAotdAdmin } from "./useAotdAdmin";

const NavBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 14px;
  background: ${colors.bgRaised};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  margin-bottom: 16px;
  @media (max-width: 640px) {
    padding: 10px 10px;
    gap: 6px;
  }
`;

const NavSeparator = styled.div`
  width: 1px;
  height: 22px;
  background: ${colors.border};
  margin: 0 4px;
`;

const DateInput = styled(Input)`
  width: 100%;
  max-width: 100%;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  @media (min-width: 641px) {
    max-width: 160px;
  }
`;

const IdInput = styled(Input)`
  flex: 1;
  min-width: 100%;
  @media (min-width: 641px) {
    min-width: 200px;
  }
`;

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  font-size: 0.82rem;
  color: ${colors.textMuted};
  @media (max-width: 640px) {
    margin-left: 0;
    flex-basis: 100%;
  }
`;

const HeaderText = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderChevron = styled.span<{ $open: boolean }>`
  transition: transform 0.15s ease;
  display: inline-block;
  color: ${colors.textMuted};
  transform: rotate(${(p) => (p.$open ? "90deg" : "0deg")});
`;

const ArtOfTheDayAdminPanel: FC = () => {
  const confirm = useConfirm();
  const {
    isAdmin,
    data,
    editable,
    setEditable,
    selectedDate,
    isLoading,
    isMissing,
    errorMsg,
    fetchToday,
    fetchByDate,
    fetchById,
    shiftDay,
    save,
    destroy,
  } = useAotdAdmin();

  const [open, setOpen] = useState(false);
  const [fetchId, setFetchId] = useState("");

  if (!isAdmin) {
    return (
      <Shell>
        <Body $open>
          <Hint>Admin access required.</Hint>
        </Body>
      </Shell>
    );
  }

  const onDelete = async () => {
    const ok = await confirm({
      title: "Delete AOTD entry?",
      message: "This action cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    await destroy();
  };

  const isToday = selectedDate === todayDayKey();

  return (
    <Shell>
      <ShellHeader onClick={() => setOpen(!open)}>
        <HeaderText>
          <HeaderChevron $open={open}>›</HeaderChevron>
          AOTD Admin · Inspector
          {data && (
            <Pill $tone="muted">
              {selectedDate} · {data.createdBy}
            </Pill>
          )}
        </HeaderText>
        <span style={{ color: colors.textMuted, fontSize: "0.82rem" }}>
          {open ? "click to hide" : "click to open"}
        </span>
      </ShellHeader>

      <Body $open={open}>
        <NavBar>
          <Btn onClick={() => shiftDay(-1)} disabled={isLoading}>
            ←
          </Btn>
          <DateInput
            type="date"
            value={selectedDate}
            onChange={(e) =>
              e.target.value && fetchByDate(e.target.value)
            }
            disabled={isLoading}
          />
          <Btn onClick={() => shiftDay(1)} disabled={isLoading}>
            →
          </Btn>
          <Btn
            $variant={isToday ? "primary" : undefined}
            onClick={fetchToday}
            disabled={isLoading}
          >
            Today
          </Btn>
          <NavSeparator />
          <IdInput
            placeholder="…or paste an AOTD _id"
            value={fetchId}
            onChange={(e) => setFetchId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && fetchId.trim()) {
                fetchById(fetchId.trim());
              }
            }}
          />
          <Btn
            onClick={() => fetchById(fetchId.trim())}
            disabled={isLoading || !fetchId.trim()}
          >
            Fetch by ID
          </Btn>
          <StatusLine>
            {isLoading
              ? "loading…"
              : data
              ? `id ${data._id.slice(-8)}`
              : isMissing
              ? "no AOTD for this day"
              : ""}
          </StatusLine>
        </NavBar>

        {errorMsg && <ErrorText>{errorMsg}</ErrorText>}

        {isMissing && !data && (
          <Hint>
            No AOTD doc exists for <strong>{selectedDate}</strong>. Try
            navigating to a different day or fetching by ID.
          </Hint>
        )}

        {data && (
          <>
            <SummaryStrip data={data} />

            <div style={{ marginTop: 22 }}>
              <SectionTitle>What happened</SectionTitle>
              <Timeline data={data} />
            </div>

            <div style={{ marginTop: 22 }}>
              <SectionTitle>Edit raw fields</SectionTitle>
              <Disclosure>
                <summary>
                  <span style={{ flex: 1 }}>Editable fields</span>
                  <Pill $tone="warn">writes save to DB</Pill>
                </summary>
                <div>
                  <EditFields
                    data={data}
                    editable={editable}
                    setEditable={setEditable}
                    isLoading={isLoading}
                    onSave={save}
                    onDelete={onDelete}
                  />
                </div>
              </Disclosure>
            </div>
          </>
        )}

        <div style={{ marginTop: 22 }}>
          <SectionTitle>Tools</SectionTitle>
          <AdminTools />
        </div>
      </Body>
    </Shell>
  );
};

export default ArtOfTheDayAdminPanel;
